// ============================================================================
// Court Splitter — Type Definitions
// ============================================================================

/** Supported currencies for room cost tracking */
export type Currency = 'USD' | 'INR' | 'EUR' | 'GBP';

/** Currency display symbols */
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  INR: '₹',
  EUR: '€',
  GBP: '£',
};

// ============================================================================
// Core Domain Types
// ============================================================================

export interface CourtRoom {
  id: string;             // 6-char room code (e.g., "B4K9X2")
  name: string;           // e.g., "Tuesday Badminton"
  createdBy: string;      // user ID (Google sub)
  createdAt: number;      // epoch ms
  updatedAt: number;      // epoch ms
  currency: Currency;
  members: RoomMember[];
  sessions: CourtSession[];
  payments: PaymentRecord[];
}

export interface RoomMember {
  id: string;             // Google sub or generated ID for manual members
  name: string;
  joinedAt: number;
  isAdmin: boolean;       // true for room creator
  isLinked: boolean;      // true if linked to a Google account
}

export interface CourtSession {
  id: string;
  date: string;           // ISO date string (YYYY-MM-DD)
  totalCost: number;
  paidBy: string;         // member ID who paid the booking
  attendees: string[];    // member IDs who played
  note?: string;
  createdBy: string;      // member ID who recorded this session
  createdAt: number;      // epoch ms
}

export interface PaymentRecord {
  id: string;
  fromMemberId: string;   // who paid
  toMemberId: string;     // who received
  amount: number;
  date: string;           // ISO date string
  note?: string;
  createdBy: string;      // who recorded this payment
  createdAt: number;      // epoch ms
}

// ============================================================================
// Calculation Types
// ============================================================================

export interface MemberBalance {
  memberId: string;
  memberName: string;
  totalPaid: number;      // total they've paid for bookings
  totalOwed: number;      // total they owe (their share of sessions attended)
  totalSettled: number;   // total settled via payment records
  netBalance: number;     // positive = others owe them, negative = they owe others
}

export interface Settlement {
  fromMemberId: string;
  fromMemberName: string;
  toMemberId: string;
  toMemberName: string;
  amount: number;
}

// ============================================================================
// Input Types (for API calls)
// ============================================================================

export interface CreateRoomInput {
  name: string;
  currency: Currency;
}

export interface SessionInput {
  date: string;
  totalCost: number;
  paidBy: string;
  attendees: string[];
  note?: string;
}

export interface PaymentInput {
  fromMemberId: string;
  toMemberId: string;
  amount: number;
  date: string;
  note?: string;
}

// ============================================================================
// Offline / Cache Types
// ============================================================================

export type PendingWriteType =
  | 'createRoom'
  | 'joinRoom'
  | 'addSession'
  | 'editSession'
  | 'deleteSession'
  | 'addMember'
  | 'removeMember'
  | 'addPayment'
  | 'deleteRoom';

export interface PendingWrite {
  id: string;
  type: PendingWriteType;
  roomCode: string;
  payload: any;
  timestamp: number;
  retryCount: number;
}

export interface LocalCache {
  rooms: CourtRoom[];
  pendingWrites: PendingWrite[];
  lastSyncTimestamp: number;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

// ============================================================================
// Real-time Event Types
// ============================================================================

export type RoomEventType =
  | 'sessionAdded'
  | 'sessionEdited'
  | 'sessionDeleted'
  | 'memberJoined'
  | 'memberRemoved'
  | 'paymentRecorded';

export interface RoomEvent {
  type: RoomEventType;
  roomCode: string;
  data: any;
  timestamp: number;
  senderId: string;
}

export interface RoomEventCallbacks {
  onSessionAdded?: (session: CourtSession) => void;
  onSessionEdited?: (session: CourtSession) => void;
  onSessionDeleted?: (sessionId: string) => void;
  onMemberJoined?: (member: RoomMember) => void;
  onMemberRemoved?: (memberId: string) => void;
  onPaymentRecorded?: (payment: PaymentRecord) => void;
}

// ============================================================================
// DynamoDB Item Types
// ============================================================================

export interface DDBRoomMetaItem {
  PK: string;            // ROOM#{roomCode}
  SK: string;            // META
  GSI1PK: string;        // USER#{creatorId}
  GSI1SK: string;        // ROOM#{roomCode}
  roomCode: string;
  name: string;
  createdBy: string;
  createdAt: string;     // ISO timestamp
  updatedAt: string;     // ISO timestamp
  currency: Currency;
  memberCount: number;
  sessionCount: number;
}

export interface DDBMemberItem {
  PK: string;            // ROOM#{roomCode}
  SK: string;            // MEMBER#{memberId}
  GSI1PK: string;        // USER#{memberId}
  GSI1SK: string;        // ROOM#{roomCode}
  memberId: string;
  name: string;
  joinedAt: string;      // ISO timestamp
  isAdmin: boolean;
  isLinked: boolean;
}

export interface DDBSessionItem {
  PK: string;            // ROOM#{roomCode}
  SK: string;            // SESSION#{sessionId}
  sessionId: string;
  date: string;
  totalCost: number;
  paidBy: string;
  attendees: string[];
  note?: string;
  createdBy: string;
  createdAt: string;     // ISO timestamp
}

export interface DDBPaymentItem {
  PK: string;            // ROOM#{roomCode}
  SK: string;            // PAYMENT#{paymentId}
  paymentId: string;
  fromMemberId: string;
  toMemberId: string;
  amount: number;
  date: string;
  note?: string;
  createdBy: string;
  createdAt: string;     // ISO timestamp
}

// ============================================================================
// Component Props
// ============================================================================

export interface CourtSplitterProps {
  onExit?: () => void;
}
